// src/components/dashboard/PaymentListReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";

// Core Guaranteed MUI Icons
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const PaymentListReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [payer, setPayer] = useState("");
  const [staff, setStaff] = useState("");
  const [paymentType, setPaymentType] = useState("None");
  const [orderBy, setOrderBy] = useState("");

  // Pagination & Empty Rows
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Clinical Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setPayer("");
    setStaff("");
    setPaymentType("None");
    setOrderBy("");
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CreditCardRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Payment List Report
            </Typography>
            <Chip
              label="Billing & Ledger"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Track all payment transactions and details
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Payments Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Payments
          </Typography>
        </Box>

        {/* Row 1: Quick Range & Date Payment Posted */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Quick Range</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
              <MenuItem value="Year to Date">Year to Date</MenuItem>
            </Select>
          </Box>

          {/* Date Payment Posted */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Date Payment Posted:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>
        </Box>

        {/* Row 2: Payer, Payment Created By Staff, Payment Type, Order Results By */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.2fr 1.2fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {/* Payer */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Payer</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={payer}
              onChange={(e) => setPayer(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select Payer(s)</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Payers</MenuItem>
              <MenuItem value="Blue Cross Blue Shield">Blue Cross Blue Shield</MenuItem>
              <MenuItem value="Refreshing">Refreshing</MenuItem>
              <MenuItem value="Self Pay">Self Pay</MenuItem>
            </Select>
          </Box>

          {/* Payment Created By Staff */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Payment Created By Staff:</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select Staff...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Staff</MenuItem>
              <MenuItem value="Awais11">Awais11</MenuItem>
              <MenuItem value="Dr. Usman Rehman">Dr. Usman Rehman</MenuItem>
              <MenuItem value="Ali Ammar">Ali Ammar</MenuItem>
            </Select>
          </Box>

          {/* Payment Type */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Payment Type</Typography>
            <Box sx={{ position: "relative" }}>
              <Select
                fullWidth
                size="small"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
                endAdornment={
                  paymentType !== "None" && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setPaymentType("None"); }} sx={{ mr: 2, color: secondaryText, p: 0.3 }}>
                      <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="None">None</MenuItem>
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Electronic Check">Electronic Check</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Insurance Claim">Insurance Claim</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Order Results By */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Order Results By:</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Date Posted">Date Posted</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Amount">Amount</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Buttons: Search & Clear */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: tealButton, boxShadow: "none" },
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 2.8,
              textTransform: "none",
              "&:hover": { borderColor: tealMain, backgroundColor: "rgba(36, 94, 101, 0.05)" },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "360px", overflowX: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: primaryText,
                    fontWeight: 800,
                    fontSize: "0.84rem",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    py: 1.8,
                    px: 2,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Date Posted</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Payer</TableCell>
                <TableCell>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}>
                    Client
                    <ArrowUpwardRoundedIcon sx={{ fontSize: "1rem", color: primaryText }} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: "30px", px: 0.5 }}>
                  <MoreVertRoundedIcon sx={{ fontSize: "1rem", color: secondaryText }} />
                </TableCell>
                <TableCell>Payment Type</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Copay Amount</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 14, textAlign: "center", borderBottom: "none" }}>
                    <Typography sx={{ color: secondaryText, fontSize: "0.9rem", fontWeight: 600 }}>
                      No rows
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem" }}>{row.datePosted}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem" }}>{row.reference}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem" }}>{row.payer}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem", fontWeight: 600 }}>{row.client}</TableCell>
                    <TableCell />
                    <TableCell sx={{ color: secondaryText, fontSize: "0.84rem" }}>{row.paymentType}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem" }}>{row.createdBy}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem" }}>{row.copayAmount}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.84rem", fontWeight: 700 }}>{row.amount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default PaymentListReport;